import { Children, type ReactNode } from "react";
import {
  Box,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  SelectTrigger,
  Separator,
} from "@salestrak/ui";
import { CurrentUserButton } from "#app/components/filters/current-user-button";
import { DateRangePresetPicker } from "#app/components/filters/date-range-preset-picker";
import { FilterValueChip } from "#app/components/filters/filter-value-chip";
import {
  DEFAULT_PRESET,
  formatPresetValueLabel,
  getPresetShortLabel,
  type DatePresetKey,
  type DatePresetOption,
  type DatePresetValue,
} from "#app/lib/utils/date-presets";
import type { Omittable } from "@salestrak/ui";

// Free dashboards rest on two windows: the stock `this-week` default AND the
// unbounded `all-time` — neither is a user deviation worth flagging. The ×
// resets to the first entry, the surface's own default.
const FREE_DASHBOARD_RESTING_KEYS: readonly DatePresetKey[] = [
  DEFAULT_PRESET,
  "all-time",
];

/**
 * THE dashboard date-window chip, every widget surface: `DateRangePresetPicker`
 * triggered by a labeled chip that ALWAYS shows the window's short label —
 * "This Week", "All time" (an icon-only rest state was tried 2026-08-31 and
 * reverted: the bare calendar icon read as an anonymous control, not the
 * board's window; the free dashboards held onto it until 2026-09-01, when it
 * read as anonymous there too). What the two states encode is BASELINE vs
 * DEVIATION: while the window is one the view would choose itself
 * (`restingKeys` — each surface passes its own) the chip is the quiet
 * outline, nothing to clear; any other window is the primary-hued outline
 * with the hover-× RESETTING to the surface default (`restingKeys[0]`) —
 * never to a window the view would not choose. The trigger is a combobox,
 * and a combobox takes no name from its content: it is named "Date range",
 * and a screen reader reads the window on its face as the value. Managed
 * homes pass their curated preset three; free dashboards take the general
 * set.
 */
export function DashboardDateRangePill({
  value,
  onChange,
  presets,
  restingKeys = FREE_DASHBOARD_RESTING_KEYS,
  align = "start",
}: Omittable<{
  value: DatePresetValue;
  onChange: (next: DatePresetValue) => void;
  /** Curated preset list for the picker (managed homes); the general set
   *  otherwise. */
  presets?: readonly DatePresetOption[];
  /** Windows that rest on the quiet outline. The first entry is the
   *  surface's own default — what the deviation state's × resets to. */
  restingKeys?: readonly DatePresetKey[];
  align?: "start" | "end";
}>) {
  const deviated = !restingKeys.includes(value.key);
  // A curated set's shortLabel wins ("This month" over the general "MTD");
  // a window carried in from elsewhere still gets the general short label.
  // A custom window shows its dates.
  const label = formatPresetValueLabel(
    value,
    presets?.find((p) => p.key === value.key)?.shortLabel ??
      getPresetShortLabel(value.key),
  );
  return (
    <DateRangePresetPicker
      value={value}
      onChange={onChange}
      presets={presets}
      align={align}
    >
      {/* The window's short label IS the face ("This Week"), so it rides the
          chip's label slot with no value; `selected` carries the deviation. */}
      <SelectTrigger
        chevron={false}
        aria-label="Date range"
        render={
          <FilterValueChip
            label={label}
            selected={deviated}
            onClear={
              deviated
                ? () => onChange({ key: restingKeys[0] ?? DEFAULT_PRESET })
                : undefined
            }
            clearLabel="Reset period"
          />
        }
      />
    </DateRangePresetPicker>
  );
}

export type DashboardFilterToolbarProps = Omittable<{
  dateRange: DatePresetValue;
  onDateRangeChange: (next: DatePresetValue) => void;
  /** Only-Me visual state + handlers — wire from `useCurrentUserFilterToggle`
   *  (via `useDashboardScopeState`). */
  onlyMeSelected: boolean;
  onOnlyMePress: () => void;
  onOnlyMeClear: () => void;
  /** The dashboard's save control — rendered first in the right-aligned row,
   *  so unsaved edits are answered before any other control. It reads as a
   *  pending decision, not a peer control, so it sits outside both clusters. */
  leading?: ReactNode;
  /** The board refresh control (`DashboardRefreshControl`) — leads the
   *  data-window cluster, since that window is exactly what it re-runs.
   *  Free dashboards pass it; managed homes have no board-level refresh. */
  refresh?: ReactNode;
  /** The quick-filter chip with the shared `FilterPanel` hanging off it —
   *  the filter set every widget's data folds in. Seated between the
   *  refresh control and Only-Me, the cross-view order the board and list
   *  toolbars keep (chip, then Only-Me). Free dashboards pass it where an
   *  `IssueFilterProvider` exists; managed homes carry no chip. */
  filter?: ReactNode;
  /** Customize actions (layout history, the Customize view gear, Add
   *  widget) — controls that change the view's persisted setup, grouped
   *  after the divider. */
  children?: ReactNode;
  /** The viewer may only READ the board (a share-only role on a shared
   *  dashboard): the band carries no data-window controls at all — no date
   *  chip, no filter chip, no Only-Me. `refresh` and `children` still
   *  render; the host decides what those hold. */
  consumeOnly?: boolean;
}>;

/**
 * The dashboard's filter toolbar — the wrapping "view" that defines the data
 * window every widget reports on (date range + Only-Me, plus the shared
 * FilterPanel's filter set behind the quick-filter chip the host passes as
 * `filter`). It is the same `DataTableToolbar` band the issue board/list
 * render below the tab strip, on the shared band grammar: FRAMING state on
 * the left (the date window — the board's Group By analogue, it decides what
 * every widget reports on), narrowing and tools right-aligned (refresh, the
 * filter chip, Only-Me, then the divider and the customize controls) — so
 * switching between List, Board, and Dashboard keeps each kind of control in
 * the same region. Pure and controlled — the route owns the state
 * (`useDashboardScopeState`) and publishes it through
 * `DashboardScopeProvider`; each widget folds it into its own query via
 * `useScopedWhere`. There is no per-widget filter and no data engine.
 */
export function DashboardFilterToolbar({
  dateRange,
  onDateRangeChange,
  onlyMeSelected,
  onOnlyMePress,
  onOnlyMeClear,
  leading,
  refresh,
  filter,
  children,
  consumeOnly = false,
}: DashboardFilterToolbarProps) {
  // The ephemeral cluster always renders (refresh is unconditional, and
  // Only-Me is for every viewer who may steer the window), so the divider
  // only depends on the customize side.
  const hasCustomize = Children.toArray(children).length > 0;

  return (
    <DataTableToolbar>
      <DataTableToolbarLeft>
        {consumeOnly ? null : (
          <DashboardDateRangePill value={dateRange} onChange={onDateRangeChange} />
        )}
      </DataTableToolbarLeft>
      <DataTableToolbarActions>
        {leading}
        <Box direction="row" align="center" gap="xs">
          {refresh}
          {/* The shared cross-view order: the quick-filter chip, then
              Only-Me — the same pair the board and list toolbars seat. A
              consume-only reader steers nothing, so neither renders. */}
          {consumeOnly ? null : filter}
          {consumeOnly ? null : (
            <CurrentUserButton
              selected={onlyMeSelected}
              onClick={onOnlyMePress}
              onClearClick={onOnlyMeClear}
              size="xs"
            />
          )}
        </Box>
        {hasCustomize ? (
          <Separator orientation="vertical" className="h-5" />
        ) : null}
        {hasCustomize ? (
          <Box direction="row" align="center" gap="xs">
            {children}
          </Box>
        ) : null}
      </DataTableToolbarActions>
    </DataTableToolbar>
  );
}
